<template>
  <div class="report-container">
    <!-- Page title with year selector -->
    <div class="page-title">
      <h1>{{ $t('reports.title') }}</h1>
      <div class="year-selector">
        <el-select v-model="mainFilters.timeRange" :placeholder="$t('reports.filters.timeRange')" size="large">
          <el-option :label="$t('reports.timeRange.allYears')" value="all_years" />
          <el-option :label="$t('reports.timeRange.currentYear')" :value="currentYear.toString()" />
          <el-option :label="$t('reports.timeRange.previousYear')" :value="(currentYear-1).toString()" />
          <el-option v-for="year in availableYears.filter(y => y !== currentYear.toString() && y !== (currentYear-1).toString())" 
            :key="year" 
            :label="$t('reports.timeRange.year', { year })" 
            :value="year" 
          />
        </el-select>
      </div>
    </div>

    <!-- Time filter indicator -->
    <div class="time-filter-indicator" v-if="report">
      <el-tag type="info" size="large" effect="plain">
        <el-icon><Calendar /></el-icon>
        <span>{{ $t('info.periodSelected') }}: {{ mainFilters.timeRange === 'all_years' ? $t('reports.timeRange.allYears') : $t('reports.timeRange.year', { year: mainFilters.timeRange }) }}</span>
      </el-tag>
    </div>

    <!-- Error Alert -->
    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      class="error-alert"
    />

    <!-- Report Data -->
    <template v-if="report">
      <!-- Summary Card -->
      <el-card class="summary-card">
        <div class="summary-header">
          <h3>
            <el-tag type="primary" size="small" effect="plain" class="time-indicator">
              <el-icon><DataLine /></el-icon> {{ $t('reports.overview.title') }}
            </el-tag>
            {{ mainFilters.timeRange === 'all_years' ? $t('reports.timeRange.allYears') : $t('reports.timeRange.year', { year: mainFilters.timeRange }) }}
          </h3>
        </div>
        <div class="summary-content">
          <div class="summary-item">
            <div class="summary-value">{{ formatCurrency(getTotalExpense) }}</div>
            <div class="summary-label">{{ $t('reports.overview.totalExpense') }}</div>
          </div>
          
          <!-- If viewing a specific year, show quarterly breakdown -->
          <template v-if="mainFilters.timeRange !== 'all_years'">
            <div class="summary-item" v-for="(quarter, index) in quarterSummary" :key="index">
              <div class="summary-value">{{ formatCurrency(quarter.total) }}</div>
              <div class="summary-label">{{ quarter.name }}</div>
            </div>
          </template>
          
          <!-- If viewing all years, show yearly breakdown -->
          <template v-else>
            <div class="summary-item" v-for="year in yearSummary" :key="year.year">
              <div class="summary-value">{{ formatCurrency(year.total_amount) }}</div>
              <div class="summary-label">{{ $t('reports.timeRange.year', { year: year.year }) }}</div>
            </div>
          </template>
        </div>
      </el-card>

      <!-- By Year / Quarter (Special chart that adapts based on view) -->
      <el-card class="chart-card">
        <template #header>
          <div class="chart-header">
            <span>
              <el-tag type="warning" size="small" effect="plain" class="time-indicator">
                <el-icon><Histogram /></el-icon> 
                {{ mainFilters.timeRange === 'all_years' ? $t('reports.charts.byTime.yearlyTitle') : $t('reports.charts.byTime.quarterlyTitle') }}
              </el-tag>
              {{ mainFilters.timeRange === 'all_years' ? $t('reports.charts.byTime.yearlyTitle') : $t('reports.charts.byTime.quarterlyTitle') }}
            </span>
          </div>
        </template>
        
        <!-- Chart-specific filters -->
        <div class="chart-filters">
          <el-select v-if="mainFilters.timeRange === 'all_years'" 
            v-model="timeChartFilters.selectedYears" 
            multiple 
            collapse-tags
            :placeholder="$t('reports.charts.byTime.selectYears')">
            <el-option
              v-for="year in getAllYearsFromData()"
              :key="year"
              :label="$t('reports.timeRange.year', { year })"
              :value="year"
            />
          </el-select>
          <el-button type="primary" size="small" @click="applyTimeChartFilters">{{ $t('common.filters.apply') }}</el-button>
        </div>
        
        <div class="chart-content">
          <div class="chart-container">
            <Bar
              :options="chartOptions"
              :data="mainFilters.timeRange === 'all_years' ? byYearData : byQuarterData"
            />
          </div>
          <div class="table-container">
            <!-- Table for years view -->
            <el-table v-if="mainFilters.timeRange === 'all_years'" 
              :data="getFilteredYearData()" 
              stripe>
              <el-table-column prop="year" :label="$t('reports.table.year')" width="100" />
              <el-table-column prop="total_amount" :label="$t('reports.table.totalAmount')">
                <template #default="scope">
                  {{ formatCurrency(scope.row.total_amount) }}
                </template>
              </el-table-column>
            </el-table>
            
            <!-- Table for quarterly view -->
            <el-table v-else :data="getFilteredQuarterData()" stripe>
              <el-table-column prop="year" :label="$t('reports.table.year')" width="100" />
              <el-table-column prop="quarter" :label="$t('reports.table.quarter')" width="100">
                <template #default="scope">
                  {{ getQuarterName(scope.row.quarter) }}
                </template>
              </el-table-column>
              <el-table-column prop="total_amount" :label="$t('reports.table.totalAmount')">
                <template #default="scope">
                  {{ formatCurrency(scope.row.total_amount) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </el-card>

      <!-- By Expense Type -->
      <el-card class="chart-card">
        <template #header>
          <div class="chart-header">
            <span>
              <el-tag type="success" size="small" effect="plain" class="time-indicator">
                <el-icon><PieChart /></el-icon> {{ $t('reports.charts.byType.title') }}
              </el-tag>
              {{ $t('reports.charts.byType.subtitle') }}
            </span>
          </div>
        </template>
        
        <!-- Chart-specific filters -->
        <div class="chart-filters">
          <el-select v-model="expenseTypeFilters.year" clearable :placeholder="$t('reports.filters.year')">
            <el-option :label="$t('reports.timeRange.allYears')" value="" />
            <el-option 
              v-for="year in getAllYearsFromData()" 
              :key="year" 
              :label="$t('reports.timeRange.year', { year })" 
              :value="year" 
            />
          </el-select>
          <el-select v-model="expenseTypeFilters.quarter" clearable :placeholder="$t('reports.filters.quarter')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option label="Q1" value="Q1" />
            <el-option label="Q2" value="Q2" />
            <el-option label="Q3" value="Q3" />
            <el-option label="Q4" value="Q4" />
          </el-select>
          <el-button type="primary" size="small" @click="applyExpenseTypeFilters">{{ $t('common.filters.apply') }}</el-button>
        </div>
        
        <div class="chart-content">
          <div class="chart-container">
            <Pie
              :options="chartOptions"
              :data="filteredExpenseTypeData"
            />
          </div>
          <div class="table-container">
            <el-table :data="filteredExpenseTypeData.datasets[0].data.length > 0 ? 
              filteredExpenseTypeData.labels.map((label, index) => ({
                expense_type: label,
                total_amount: filteredExpenseTypeData.datasets[0].data[index]
              })) : []" 
              stripe>
              <el-table-column prop="expense_type" :label="$t('reports.table.expenseType')">
                <template #default="scope">
                  {{ scope.row.expense_type || $t('common.undefined') }}
                </template>
              </el-table-column>
              <el-table-column prop="total_amount" :label="$t('reports.table.totalAmount')">
                <template #default="scope">
                  {{ formatCurrency(scope.row.total_amount) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </el-card>

      <!-- By Category -->
      <el-card class="chart-card">
        <template #header>
          <div class="chart-header">
            <span>
              <el-tag type="danger" size="small" effect="plain" class="time-indicator">
                <el-icon><Folder /></el-icon> {{ $t('reports.charts.byCategory.title') }}
              </el-tag>
              {{ $t('reports.charts.byCategory.subtitle') }}
            </span>
          </div>
        </template>
        
        <!-- Chart-specific filters -->
        <div class="chart-filters">
          <el-select v-model="categoryFilters.year" clearable :placeholder="$t('reports.filters.year')">
            <el-option :label="$t('reports.timeRange.allYears')" value="" />
            <el-option 
              v-for="year in getAllYearsFromData()" 
              :key="year" 
              :label="$t('reports.timeRange.year', { year })" 
              :value="year" 
            />
          </el-select>
          <el-select v-model="categoryFilters.quarter" clearable :placeholder="$t('reports.filters.quarter')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option label="Q1" value="Q1" />
            <el-option label="Q2" value="Q2" />
            <el-option label="Q3" value="Q3" />
            <el-option label="Q4" value="Q4" />
          </el-select>
          <el-select v-model="categoryFilters.expense_type" clearable :placeholder="$t('reports.filters.expenseType')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option 
              v-for="type in getUniqueExpenseTypes()" 
              :key="type" 
              :label="type || $t('common.undefined')" 
              :value="type" 
            />
          </el-select>
          <el-button type="primary" size="small" @click="applyCategoryFilters">{{ $t('common.filters.apply') }}</el-button>
        </div>
        
        <div class="chart-content">
          <div class="chart-container">
            <Pie
              :options="chartOptions"
              :data="filteredCategoryData"
            />
          </div>
          <div class="table-container">
            <el-table :data="filteredCategoryData.datasets[0].data.length > 0 ? 
              filteredCategoryData.labels.map((label, index) => ({
                category: label,
                total_amount: filteredCategoryData.datasets[0].data[index]
              })) : []" 
              stripe>
              <el-table-column prop="category" :label="$t('reports.table.category')" />
              <el-table-column prop="total_amount" :label="$t('reports.table.totalAmount')">
                <template #default="scope">
                  {{ formatCurrency(scope.row.total_amount) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </el-card>

      <!-- By Account Holder -->
      <el-card class="chart-card">
        <template #header>
          <div class="chart-header">
            <span>
              <el-tag type="info" size="small" effect="plain" class="time-indicator">
                <el-icon><User /></el-icon> {{ $t('reports.charts.byAccountHolder.title') }}
              </el-tag>
              {{ $t('reports.charts.byAccountHolder.subtitle') }}
            </span>
          </div>
        </template>
        
        <!-- Chart-specific filters -->
        <div class="chart-filters">
          <el-select v-model="accountHolderFilters.year" clearable :placeholder="$t('reports.filters.year')">
            <el-option :label="$t('reports.timeRange.allYears')" value="" />
            <el-option 
              v-for="year in getAllYearsFromData()" 
              :key="year" 
              :label="$t('reports.timeRange.year', { year })" 
              :value="year" 
            />
          </el-select>
          <el-select v-model="accountHolderFilters.quarter" clearable :placeholder="$t('reports.filters.quarter')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option label="Q1" value="Q1" />
            <el-option label="Q2" value="Q2" />
            <el-option label="Q3" value="Q3" />
            <el-option label="Q4" value="Q4" />
          </el-select>
          <el-select v-model="accountHolderFilters.expense_type" clearable :placeholder="$t('reports.filters.expenseType')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option 
              v-for="type in getUniqueExpenseTypes()" 
              :key="type" 
              :label="type || $t('common.undefined')" 
              :value="type" 
            />
          </el-select>
          <el-select v-model="accountHolderFilters.category" clearable :placeholder="$t('reports.filters.category')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option 
              v-for="cat in getUniqueCategories()" 
              :key="cat" 
              :label="cat" 
              :value="cat" 
            />
          </el-select>
          <el-button type="primary" size="small" @click="applyAccountHolderFilters">{{ $t('common.filters.apply') }}</el-button>
        </div>
        
        <div class="chart-content">
          <div class="chart-container">
            <Bar
              :options="chartOptions"
              :data="filteredAccountHolderData"
            />
          </div>
          <div class="table-container">
            <el-table :data="filteredAccountHolderData.datasets[0].data.length > 0 ? 
              filteredAccountHolderData.labels.map((label, index) => ({
                account_holder: label,
                total_amount: filteredAccountHolderData.datasets[0].data[index]
              })) : []" 
              stripe>
              <el-table-column prop="account_holder" :label="$t('reports.table.accountHolder')" />
              <el-table-column prop="total_amount" :label="$t('reports.table.totalAmount')">
                <template #default="scope">
                  {{ formatCurrency(scope.row.total_amount) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </el-card>

      <!-- Detailed Expenses (with own filters) -->
      <el-card class="chart-card">
        <template #header>
          <div class="chart-header">
            <span>
              <el-tag type="primary" size="small" effect="plain" class="time-indicator">
                <el-icon><Tickets /></el-icon> {{ $t('reports.charts.detailedExpenses.title') }}
              </el-tag>
              {{ $t('reports.charts.detailedExpenses.subtitle') }}
            </span>
          </div>
        </template>
        
        <!-- Chart-specific filters -->
        <div class="chart-filters">
          <el-date-picker
            v-model="expensesFilters.dateRange"
            type="daterange"
            range-separator="đến"
            start-placeholder="$t('common.filters.startDate')"
            end-placeholder="$t('common.filters.endDate')"
            format="DD/MM/YYYY"
          />
          <el-select v-model="expensesFilters.expense_type" clearable :placeholder="$t('reports.filters.expenseType')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option 
              v-for="type in getUniqueExpenseTypes()" 
              :key="type" 
              :label="type || $t('common.undefined')" 
              :value="type" 
            />
          </el-select>
          <el-select v-model="expensesFilters.category" clearable :placeholder="$t('reports.filters.category')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option 
              v-for="cat in getUniqueCategories()" 
              :key="cat" 
              :label="cat" 
              :value="cat" 
            />
          </el-select>
          <el-select v-model="expensesFilters.account_holder" clearable :placeholder="$t('reports.filters.accountHolder')">
            <el-option :label="$t('common.filters.all')" value="" />
            <el-option 
              v-for="holder in getUniqueAccountHolders()" 
              :key="holder" 
              :label="holder" 
              :value="holder" 
            />
          </el-select>
          <el-button type="primary" size="small" @click="applyExpensesFilters">{{ $t('common.filters.apply') }}</el-button>
        </div>
        
        <el-table :data="filteredExpenses" stripe>
          <el-table-column 
            prop="payment_date" 
            :label="$t('reports.table.paymentDate')" 
            width="130" 
            sortable 
          />
          <el-table-column prop="expense_type" :label="$t('reports.table.expenseType')" width="150">
            <template #default="scope">
              {{ scope.row.expense_type || $t('common.undefined') }}
            </template>
          </el-table-column>
          <el-table-column prop="category" :label="$t('reports.table.category')" width="150" />
          <el-table-column prop="description" :label="$t('reports.table.description')" show-overflow-tooltip />
          <el-table-column prop="amount" :label="$t('reports.table.amount')" width="130">
            <template #default="scope">
              {{ formatCurrency(scope.row.amount) }}
            </template>
          </el-table-column>
          <el-table-column prop="account_holder" :label="$t('reports.table.accountHolder')" width="150" />
          <el-table-column prop="status" :label="$t('reports.table.status')" width="100">
            <template #default="scope">
              <el-tag
                :type="getStatusType(scope.row.status)"
                size="small"
              >
                {{ scope.row.status }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { Document, Search, Calendar, QuestionFilled, PieChart, DataLine, Histogram, Tickets, User, Money, Folder } from '@element-plus/icons-vue'
import { Bar, Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
} from 'chart.js'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { API_URLS, API_VERSION } from '@/config'

// Register ChartJS components
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement)

// Get i18n instance
const { t, te } = useI18n()

// Check if keys exist
onMounted(() => {
  console.log('Checking i18n keys:')
  console.log('reports.table.category exists:', te('reports.table.category'))
  console.log('reports.table.totalAmount exists:', te('reports.table.totalAmount'))
})

interface QuarterData {
  quarter: string;
  year: string;
  total_amount: number;
}

interface ExpenseTypeData {
  expense_type: string;
  total_amount: number;
}

interface CategoryData {
  category: string;
  total_amount: number;
}

interface AccountHolderData {
  account_holder: string;
  total_amount: number;
}

interface Expense {
  id: string;
  payment_date: string;
  expense_type?: string;
  category: string;
  description?: string;
  amount: number;
  account_holder: string;
  status: string;
}

interface Report {
  byQuarter: QuarterData[];
  byExpenseType: ExpenseTypeData[];
  byCategory: CategoryData[];
  byAccountHolder: AccountHolderData[];
  expenses: Expense[];
}

// Get current year and range of 5 years for dropdown
const currentYear = new Date().getFullYear()
const availableYears = computed(() => {
  const years = []
  for (let i = currentYear - 4; i <= currentYear; i++) {
    years.push(i.toString())
  }
  return years
})

// State
const report = ref<Report | null>(null)
const error = ref('')

// Main time range filter
const mainFilters = ref({
  timeRange: 'all_years', // Default to all years
  year: currentYear.toString() // Added year property
})

// Chart-specific filters
const timeChartFilters = ref({
  selectedYears: [] as string[] // Empty means all years
})

const expenseTypeFilters = ref({
  year: '',
  quarter: '',
})

const categoryFilters = ref({
  year: '',
  quarter: '',
  expense_type: '',
})

const accountHolderFilters = ref({
  year: '',
  quarter: '',
  expense_type: '',
  category: '',
})

const expensesFilters = ref({
  dateRange: null as [Date, Date] | null,
  expense_type: '',
  category: '',
  account_holder: '',
})

// Helper function to get all years from the data
const getAllYearsFromData = () => {
  if (!report.value) return []
  
  const years = new Set<string>()
  
  // Add years from quarter data
  report.value.byQuarter.forEach(item => {
    years.add(item.year)
  })
  
  return Array.from(years).sort((a, b) => parseInt(b) - parseInt(a)) // Sort descending
}

// Helper functions to get unique values for filter dropdowns
const getUniqueExpenseTypes = () => {
  if (!report.value) return []
  
  const types = new Set<string>()
  report.value.byExpenseType.forEach(item => {
    types.add(item.expense_type)
  })
  
  return Array.from(types)
}

const getUniqueCategories = () => {
  if (!report.value) return []
  
  const categories = new Set<string>()
  report.value.byCategory.forEach(item => {
    categories.add(item.category)
  })
  
  return Array.from(categories)
}

const getUniqueAccountHolders = () => {
  if (!report.value) return []
  
  const holders = new Set<string>()
  report.value.byAccountHolder.forEach(item => {
    holders.add(item.account_holder)
  })
  
  return Array.from(holders)
}

// Filter application functions
const applyTimeChartFilters = () => {
  // No need to fetch data, just update the filtered view
}

const applyExpenseTypeFilters = () => {
  // No need to fetch data, just update the filtered view
}

const applyCategoryFilters = () => {
  // No need to fetch data, just update the filtered view
}

const applyAccountHolderFilters = () => {
  // No need to fetch data, just update the filtered view
}

const applyExpensesFilters = () => {
  // No need to fetch data, just update the filtered view
}

// Data aggregation functions
// Get data by year (aggregating quarterly data)
const getYearlyData = computed(() => {
  if (!report.value) return []
  
  const yearMap = new Map<string, number>()
  
  report.value.byQuarter.forEach(item => {
    const year = item.year
    const amount = item.total_amount
    
    if (yearMap.has(year)) {
      yearMap.set(year, yearMap.get(year)! + amount)
    } else {
      yearMap.set(year, amount)
    }
  })
  
  // Convert to array and sort by year
  return Array.from(yearMap.entries())
    .map(([year, total_amount]) => ({ year, total_amount }))
    .sort((a, b) => parseInt(b.year) - parseInt(a.year))
})

// Computed properties for filtered data
const getFilteredYearData = () => {
  if (timeChartFilters.value.selectedYears.length === 0) {
    return getYearlyData.value
  }
  
  return getYearlyData.value.filter(
    item => timeChartFilters.value.selectedYears.includes(item.year)
  )
}

const getFilteredQuarterData = () => {
  if (!report.value) return []
  
  // Only show quarters for the selected year
  return report.value.byQuarter.filter(
    item => item.year === mainFilters.value.timeRange
  )
}

// Computed properties for summary data
const getTotalExpense = computed(() => {
  if (!report.value) return 0
  
  if (mainFilters.value.timeRange === 'all_years') {
    // Sum all expenses across all years
    return report.value.byQuarter.reduce((acc, curr) => acc + curr.total_amount, 0)
  } else {
    // Sum expenses for the selected year only
    return report.value.byQuarter
      .filter(q => q.year === mainFilters.value.timeRange)
      .reduce((acc, curr) => acc + curr.total_amount, 0)
  }
})

const yearSummary = computed(() => {
  return getYearlyData.value
})

const quarterSummary = computed(() => {
  if (!report.value) return []
  
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4']
  return quarters.map(q => {
    const quarterData = report.value?.byQuarter.find(
      item => item.quarter === q && item.year === mainFilters.value.timeRange
    )
    return {
      name: getQuarterName(q),
      total: quarterData?.total_amount || 0
    }
  })
})

// Chart data
const byYearData = computed(() => {
  const filteredData = getFilteredYearData()
  
  return {
    labels: filteredData.map(item => `Năm ${item.year}`),
    datasets: [
      {
        label: 'Tổng chi phí theo năm',
        data: filteredData.map(item => item.total_amount),
        backgroundColor: 'rgba(64, 158, 255, 0.6)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 1,
      },
    ],
  }
})

const byQuarterData = computed(() => {
  if (!report.value) return { labels: [], datasets: [] }
  
  // Filter by selected year
  const filteredData = report.value.byQuarter.filter(
    item => item.year === mainFilters.value.timeRange
  )
  
  return {
    labels: filteredData.map(item => {
      const quarterMap: Record<string, string> = {
        'Q1': 'Quý 1 (T1-3)',
        'Q2': 'Quý 2 (T4-6)',
        'Q3': 'Quý 3 (T7-9)',
        'Q4': 'Quý 4 (T10-12)'
      }
      return quarterMap[item.quarter] || item.quarter
    }),
    datasets: [
      {
        label: `Tổng chi phí năm ${mainFilters.value.timeRange}`,
        data: filteredData.map(item => item.total_amount),
        backgroundColor: 'rgba(64, 158, 255, 0.6)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 1,
      },
    ],
  }
})

// Filtered data for each chart based on its own filters
const filteredExpenseTypeData = computed(() => {
  if (!report.value) return { labels: [], datasets: [] }
  
  // Apply filters
  let data = [...report.value.byExpenseType]
  
  // TODO: Apply actual filtering based on expenses matching the criteria
  // For now, we'll return all data since filtering would require cross-referencing with expenses
  
  return {
    labels: data.map(item => item.expense_type || 'Không xác định'),
    datasets: [
      {
        label: 'Tổng chi phí theo loại',
        data: data.map(item => item.total_amount),
        backgroundColor: [
          'rgba(64, 158, 255, 0.6)',
          'rgba(103, 194, 58, 0.6)',
          'rgba(230, 162, 60, 0.6)',
          'rgba(245, 108, 108, 0.6)',
          'rgba(144, 147, 153, 0.6)',
        ],
        borderColor: [
          'rgba(64, 158, 255, 1)',
          'rgba(103, 194, 58, 1)',
          'rgba(230, 162, 60, 1)',
          'rgba(245, 108, 108, 1)',
          'rgba(144, 147, 153, 1)',
        ],
        borderWidth: 1,
      },
    ],
  }
})

const filteredCategoryData = computed(() => {
  if (!report.value) return { labels: [], datasets: [] }
  
  // Apply filters
  let data = [...report.value.byCategory]
  
  // TODO: Apply actual filtering based on expenses matching the criteria
  // For now, we'll return all data since filtering would require cross-referencing with expenses
  
  return {
    labels: data.map(item => item.category),
    datasets: [
      {
        label: 'Tổng chi phí theo danh mục',
        data: data.map(item => item.total_amount),
        backgroundColor: [
          'rgba(64, 158, 255, 0.6)',
          'rgba(103, 194, 58, 0.6)',
          'rgba(230, 162, 60, 0.6)',
          'rgba(245, 108, 108, 0.6)',
          'rgba(144, 147, 153, 0.6)',
        ],
        borderColor: [
          'rgba(64, 158, 255, 1)',
          'rgba(103, 194, 58, 1)',
          'rgba(230, 162, 60, 1)',
          'rgba(245, 108, 108, 1)',
          'rgba(144, 147, 153, 1)',
        ],
        borderWidth: 1,
      },
    ],
  }
})

const filteredAccountHolderData = computed(() => {
  if (!report.value) return { labels: [], datasets: [] }
  
  // Apply filters
  let data = [...report.value.byAccountHolder]
  
  // TODO: Apply actual filtering based on expenses matching the criteria
  // For now, we'll return all data since filtering would require cross-referencing with expenses
  
  return {
    labels: data.map(item => item.account_holder),
    datasets: [
      {
        label: 'Tổng chi phí theo chủ tài khoản',
        data: data.map(item => item.total_amount),
        backgroundColor: 'rgba(103, 194, 58, 0.6)',
        borderColor: 'rgba(103, 194, 58, 1)',
        borderWidth: 1,
      },
    ],
  }
})

const filteredExpenses = computed(() => {
  if (!report.value) return []
  
  let expenses = [...report.value.expenses]
  
  // Apply date range filter
  if (expensesFilters.value.dateRange) {
    const [startDate, endDate] = expensesFilters.value.dateRange
    
    if (startDate && endDate) {
      // Format dates to ISO strings for comparison
      const startStr = startDate.toISOString().split('T')[0]
      const endStr = endDate.toISOString().split('T')[0]
      
      expenses = expenses.filter(exp => {
        const expDate = exp.payment_date
        return expDate >= startStr && expDate <= endStr
      })
    }
  }
  
  // Apply expense type filter
  if (expensesFilters.value.expense_type) {
    expenses = expenses.filter(exp => 
      exp.expense_type === expensesFilters.value.expense_type
    )
  }
  
  // Apply category filter
  if (expensesFilters.value.category) {
    expenses = expenses.filter(exp => 
      exp.category === expensesFilters.value.category
    )
  }
  
  // Apply account holder filter
  if (expensesFilters.value.account_holder) {
    expenses = expenses.filter(exp => 
      exp.account_holder === expensesFilters.value.account_holder
    )
  }
  
  return expenses
})

// Utility functions
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount)
}

const getStatusType = (status: string): string => {
  const statusMap: Record<string, string> = {
    completed: 'success',
    pending: 'warning',
    failed: 'danger'
  }
  return statusMap[status] || 'info'
}

const getQuarterName = (quarter: string) => {
  const quarterMap: Record<string, string> = {
    'Q1': 'Quý 1 (T1-3)',
    'Q2': 'Quý 2 (T4-6)',
    'Q3': 'Quý 3 (T7-9)',
    'Q4': 'Quý 4 (T10-12)'
  }
  return quarterMap[quarter] || quarter
}

// Chart options
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    title: {
      display: true,
      text: 'Dữ liệu chi phí',
      font: {
        size: 14
      }
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.dataset.label || ''
          const value = context.raw
          return `${label}: ${formatCurrency(value)}`
        }
      },
    },
  },
}

// Fetch data
const fetchReport = async () => {
  try {
    error.value = '' // Reset error message
    
    let url = `${API_URLS.report}/${API_VERSION}/report`
    
    if (mainFilters.value.timeRange === 'specific_year' && mainFilters.value.year) {
      url += `?year=${mainFilters.value.year}`
    }
    
    const response = await axios.get(url)
    report.value = response.data
  } catch (err: any) {
    error.value = err.response?.data?.error || 'Không thể tải dữ liệu báo cáo'
    report.value = null
    ElMessage.error(error.value)
  }
}

// Fetch report data on mount
onMounted(() => {
  fetchReport()
})
</script>

<style scoped>
.report-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.filter-card {
  margin-bottom: 10px;
}

.filter-title {
  margin-bottom: 16px;
}

.filter-title h3 {
  margin: 0;
  font-size: 18px;
  color: var(--el-text-color-primary);
}

.filter-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-grid {
  display: flex;
  gap: 16px;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.error-alert {
  margin-bottom: 20px;
}

.chart-card {
  margin-bottom: 20px;
}

.chart-header {
  font-size: 18px;
  font-weight: bold;
}

.chart-filters {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
  width: 100%;
}

.chart-filters .el-select {
  width: 200px;
}

.chart-filters .el-date-picker {
  width: 350px;
}

.chart-filters .el-button {
  margin-left: auto;
}

.chart-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.chart-container {
  height: 300px;
  position: relative;
}

.table-container {
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

/* Summary card styling */
.summary-card {
  margin-bottom: 20px;
}

.summary-header {
  margin-bottom: 16px;
}

.summary-header h3 {
  font-size: 18px;
  margin: 0;
  color: var(--el-text-color-primary);
}

.summary-content {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.summary-item {
  flex: 1;
  min-width: 150px;
  background-color: var(--el-color-primary-light-9);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-value {
  font-size: 20px;
  font-weight: bold;
  color: var(--el-color-primary);
  margin-bottom: 8px;
}

.summary-label {
  font-size: 14px;
  color: var(--el-text-color-secondary);
}

/* Time filter indicator styling */
.time-filter-indicator {
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.time-filter-indicator .el-tag {
  padding: 8px 16px;
  font-size: 16px;
}

.time-filter-indicator .el-icon {
  margin-right: 8px;
  vertical-align: middle;
}

/* Time indicator styling */
.time-indicator {
  margin-right: 8px;
  vertical-align: middle;
  font-weight: normal;
}

.time-indicator .el-icon {
  margin-right: 4px;
  vertical-align: middle;
}

.help-icon {
  margin-left: 6px;
  font-size: 14px;
  color: var(--el-text-color-secondary);
  cursor: help;
}

@media (max-width: 1200px) {
  .chart-filters {
    flex-direction: row;
    flex-wrap: wrap;
  }
  
  .chart-filters .el-select {
    width: calc(50% - 8px);
    min-width: 150px;
  }
  
  .chart-filters .el-date-picker {
    width: 100%;
  }
  
  .chart-filters .el-button {
    margin-left: 0;
  }
}

@media (max-width: 1024px) {
  .chart-content {
    grid-template-columns: 1fr;
  }
  
  .summary-item {
    min-width: 120px;
  }
}

@media (max-width: 640px) {  
  .summary-content {
    flex-direction: column;
  }
}

/* Add styles for the new page title layout */
.page-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--el-border-color-light);
  padding-bottom: 16px;
}

.page-title h1 {
  margin: 0;
  font-size: 24px;
  color: var(--el-text-color-primary);
}

.year-selector {
  min-width: 200px;
}

/* Rename the header style for each chart section */
.chart-section-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--el-border-color-light);
  padding-bottom: 12px;
}

.chart-section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
}
</style> 